import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { PageHead } from "@/components/PageHead";
import { useQuery } from "@tanstack/react-query";

import { assetData, type AssetDataRecord, type AssetDataSignal, type AssetDataVal, type AssetDataLatest } from "@/api/client";
import { useCursorTail } from "@/lib/hooks/useCursorTail";
import { useSticky } from "@/lib/hooks/useSticky";
import { ago, POLL } from "@/lib/format";
import { ContextStrip } from "@/modules/asset_data/ContextStrip";
import { DevicePicker } from "@/modules/asset_data/picker";
import { RecordTable, fmt } from "@/modules/asset_data/RecordTable";
import { BalanceBar } from "@/modules/asset_data/Gauge";
import { VehicleControlCard } from "@/modules/asset_data/VehicleControlCard";
import { Meter, Panel, Primary, Reference, Secondary, row }
  from "@/modules/asset_data/Tiers";

import { Icon } from "@/components/icons";
// Hard cap on rows held in the browser for the live tail. Bounds RAM on a tab
// left open for hours: at the 5–15 s snapshot cadence this is ~15–30 min of
// scrollback, and the buffer NEVER grows past it (oldest rows drop off).
const MAX_ROWS = 150;

// ---- tiny presentational helpers -------------------------------------------

/** How many of these registers are reporting something. `null`/absent is NOT
 *  a fault — it is a register this firmware does not send, and counting it
 *  would put a permanent warning on every older device. */
function faultCount(v: Vals, keys: string[]): number {
  return keys.filter((k) => { const n = num(v, k); return n != null && n !== 0; }).length;
}


/** Red/green pill for a bitfield fault register. 0 = OK (green), non-0 = fault
 *  (red). `hint` is the plain-English meaning, shown on hover. */
function FaultChip({ label, bits, hint }: { label: string; bits: number | null | undefined; hint: string }) {
  const ok = bits == null || bits === 0;
  const col = ok ? "var(--ok)" : "var(--bad)";
  const title = `${hint} - ${ok ? "OK, no bits set" : `FAULT 0x${(bits as number).toString(16)}`}`;
  return (
    <span className="chip" title={title} style={{ color: col, background: `color-mix(in srgb, ${col} 13%, transparent)`, fontSize: 10.5, cursor: "help" }}>
      {ok ? "✓" : "⚠"} {label}{ok ? "" : ` 0x${(bits as number).toString(16)}`}
    </span>
  );
}

/** Neutral info pill with a hover explanation (cursor: help). */
function InfoChip({ children, hint }: { children: ReactNode; hint: string }) {
  return (
    <span className="chip" title={hint} style={{ fontSize: 10.5, color: "var(--t-mute)", background: "var(--well)", cursor: "help" }}>
      {children}
    </span>
  );
}

// ---- value access + scaling -------------------------------------------------

type Vals = Record<string, AssetDataVal>;
function num(v: Vals, k: string): number | null {
  const x = v[k];
  return typeof x === "number" ? x : null;
}
function str(v: Vals, k: string): string | null {
  const x = v[k];
  return x == null || x === "" ? null : String(x);
}
const dash = (x: ReactNode) => (x == null || x === "" ? <span style={{ color: "var(--t-dim)" }}>—</span> : x);
// deci-unit → real unit (temps, alt, hdop, gps speed are all ×10 on the wire)
const deci = (v: Vals, k: string) => { const n = num(v, k); return n == null ? null : n / 10; };

const FIX_LABEL: Record<number, string> = { 0: "no fix", 2: "2D fix", 3: "3D fix" };

// ============================================================================
//  DASHBOARD  — Device / Battery / Motor sections, then the record table
// ============================================================================

function Dashboard({ imei }: { imei: string }) {
  const q = useQuery({ queryKey: ["live", "latest", imei], queryFn: () => assetData.latest(imei), refetchInterval: POLL.STATE });
  const l = q.data?.latest as AssetDataLatest | undefined;
  if (!l) return <div className="panel panel-pad cap">No snapshot for this device yet.</div>;
  const v = l.values;

  // LOCATION, FROM THE SERVER'S `position` BLOCK — not from `values`.
  //
  // `values.lat/lng` is the PACKET as decoded, and it is correctly NULL when
  // the device reported no fix (D-07). Reading it here is what made the card
  // say "No GPS fix yet, location unavailable" for a bike that had been
  // parked in the same spot for an hour: true about the packet, useless about
  // the asset. `position` answers the other question — where it was last, and
  // how long ago — and says which of the two it is handing back.
  const pos = l.position;
  const hasGps = pos?.ever && pos.lat != null && pos.lng != null;
  const liveFix = !!(hasGps && pos.fix);
  const mapUrl = hasGps ? `https://www.google.com/maps/search/?api=1&query=${pos.lat},${pos.lng}` : null;
  const fix = num(v, "gps_fix") ?? 0;

  const packI = num(v, "pack_i");
  const charging = packI != null && packI < 0;
  const soc = num(v, "soc");
  const speed = num(v, "speed_kmph");

  // SoC IS A MAGNITUDE THAT IS ALSO A STATUS, which is the one case a value
  // earns a status colour. Everything else on this page stays in text ink:
  // a dashboard where every number is coloured has not told you which to
  // look at.
  const socTone = soc == null ? undefined
    : soc <= 15 ? "var(--bad)" : soc <= 35 ? "var(--warn)" : undefined;

  return (
    <>
      {/* ═══════════ IDENTITY BAR ═══════════
          Who this is, and the two clocks. Deliberately quiet: it is the
          page's address label, not its content. */}
      <div className="panel panel-pad" style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        <span className="mono" style={{ color: "var(--t-bright)", fontWeight: 700, fontSize: 15, letterSpacing: -0.2 }}>{l.imei}</span>
        {/* ONLINE IS STATUS, so it keeps a status colour: with a glyph and a
            word, because `--ok` and `--warn` are ΔE 4.1 apart under
            protanopia and the colour alone is not legible to every reader. */}
        <span className="chip" style={{
          color: l.online ? "var(--ok)" : "var(--warn)",
          background: l.online ? "var(--ok-soft)" : "var(--warn-soft)",
        }}>{l.online ? "● ONLINE" : "○ STALE"}</span>
        {/* app version is NOT status. It was wearing `--info`, the info
            colour, for no reason other than that blue looked fine. */}
        {l.fw_ver && <span className="chip" style={{ color: "var(--t-mute)", background: "var(--well)" }}>app {l.fw_ver}</span>}
        <ContextStrip imei={l.imei} />
        {l.company_id && <span className="muted" style={{ fontSize: 11.5 }}>· {l.company_id === "__inventory__" ? "inventory" : l.company_id}</span>}
        <span className="muted" style={{ marginLeft: "auto", fontSize: 11.5 }}>
          {/* TWO CLOCKS, never collapsed into one: the DEVICE spoke `seen` ago,
              the POSITION was true `GPS` ago. A bike parked indoors is online
              with an hour-old fix, and one number cannot say which it is. */}
          seq {l.seq ?? "—"} · seen {ago(l.last_seen)} ago
          {pos?.at ? <> · GPS {ago(pos.at)} ago{!pos.fix ? " (last known)" : ""}</> : null}
        </span>
      </div>

      {/* ═══════════ THE HERO ═══════════
          The question the page exists to answer, ahead of board IDs and GPS:
          can the bike in front of me move? Everything below is context. */}
      <VehicleControlCard vc={l.vehicle_control} fresh={l.online} />

      {/* ═══════════ TIER 1: the live row ═══════════
          Four readings someone acts on. They used to be scattered: SoC and
          speed behind dials in two different cards, location in a third, each
          at the same weight as a boot version. */}
      <Panel title="Right now" lead>
        <div style={row(150, "18px 28px")}>
          <Primary label="State of charge" value={soc != null ? Math.round(soc) : "—"} unit="%"
            tone={socTone}
            sub={<Meter frac={soc == null ? null : soc / 100}
              tone={socTone ?? "var(--teal)"} />}
            hint="Battery state of charge, as the BMS reports it" />
          <Primary label="Speed" value={speed != null ? Math.round(speed) : "—"} unit="km/h"
            sub={<Meter frac={speed == null ? null : speed / 120} />}
            hint="Road speed. The dial it replaces read to 120 km/h." />
          <Primary label="Pack" value={fmt(num(v, "pack_v") ?? "—")} unit="V"
            sub={packI == null ? undefined
              : <span style={{ color: charging ? "var(--ok)" : undefined }}>
                  {charging ? "↓ charging" : "↑ drawing"} {Math.abs(packI).toFixed(1)} A
                </span>}
            hint="Pack voltage, and the current with its direction" />
          <Primary label="Odometer"
            value={num(v, "odometer_m") != null ? (num(v, "odometer_m")! / 1000).toFixed(1) : "—"}
            unit="km"
            sub={num(v, "trip_m") != null ? `trip ${(num(v, "trip_m")! / 1000).toFixed(1)} km` : undefined} />
        </div>
      </Panel>

      {/* ═══════════ LOCATION ═══════════ */}
      {/* THE WORDING HERE IS NOT MINE TO RESTYLE. "Live location", "Last
          known location" and "where it was N ago" were chosen after the card
          told an operator a parked bike had no location at all, and
          `Position.test.tsx` asserts each of them because they are the three
          BRANCHES a text test cannot tell apart. The redesign changes the
          weight and the colour of this card; the sentences stay. */}
      <Panel title={liveFix ? "Live location" : hasGps ? "Last known location" : "Location"}
        right={mapUrl ? (
          <a href={mapUrl} target="_blank" rel="noopener noreferrer" className="btn ghost"
            title="Open this position in Google Maps">Open in Maps ↗</a>
        ) : undefined}>
        {mapUrl ? (
          <>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              <span className="mono" style={{ fontSize: 22, fontWeight: 700, color: "var(--t-bright)", letterSpacing: -0.3 }}>
                {pos.lat!.toFixed(5)}, {pos.lng!.toFixed(5)}
              </span>
              {/* THE STALENESS INDICATOR. A position with no age beside it
                  reads as "now", and that is the whole failure this card had:
                  a coordinate on screen is taken as current unless something
                  says otherwise. NOT having a fix IS a status, so this one
                  keeps its status colour. */}
              {liveFix
                ? <span className="muted" style={{ fontSize: 11.5 }}>fix as of {ago(pos.at)} ago</span>
                : <span className="chip" style={{ color: "var(--warn)", background: "var(--warn-soft)", fontSize: 10.5 }}>
                    ○ no fix right now · where it was {ago(pos.at)} ago
                  </span>}
            </div>
            <div style={row(120)}>
              <Secondary label="Fix" value={FIX_LABEL[fix] ?? "—"} />
              <Secondary label="Satellites" value={fmt(num(v, "gps_sat") ?? "—")} />
              <Secondary label="HDOP" value={fmt(deci(v, "gps_hdop_d") ?? "—")} />
              <Secondary label="Altitude" value={fmt(deci(v, "gps_alt_dm") ?? "—")} unit="m" />
            </div>
          </>
        ) : (
          <div className="cap">
            No GPS fix has EVER been reported for this device, so there is no
            last known position to show.
          </div>
        )}
      </Panel>

      {/* ═══════════ TIER 2: battery detail ═══════════ */}
      <Panel title="Battery"
        faults={faultCount(v, ["battery_err_bits", "batt_alarm_bits"])}
        note={charging ? "charging" : "discharging"}>
        <div style={row(118)}>
          <Secondary label="Health" value={fmt(num(v, "batt_soh_pct") ?? "—")} unit="%" hint="State of health" />
          <Secondary label="Temp" value={fmt(deci(v, "battery_temp_decic") ?? num(v, "temp_max_c") ?? "—")} unit="°C" />
          <Secondary label="Cycles" value={fmt(num(v, "batt_cycle_count") ?? "—")} />
          <Secondary label="Capacity" value={fmt(num(v, "batt_capacity_mah") ?? "—")} unit="mAh" />
          <Secondary label="Cells" value={fmt(num(v, "cells") ?? "—")} />
          <Secondary label="Cell spread" value={fmt(num(v, "cell_delta_mv") ?? "—")} unit="mV"
            hint="Max cell minus min cell. A widening spread is the early sign of a failing cell." />
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.9, textTransform: "uppercase", color: "var(--t-mute)", marginBottom: 6 }}>
            Cell balance {num(v, "cell_min_idx") != null ? `· weak cell #${num(v, "cell_min_idx")}` : ""}
          </div>
          <BalanceBar minMv={num(v, "cell_min_mv")} maxMv={num(v, "cell_max_mv")} />
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 14 }}>
          <FaultChip label="Batt" bits={num(v, "battery_err_bits")} hint="Battery fault register" />
          <FaultChip label="Alarms" bits={num(v, "batt_alarm_bits")} hint="Battery alarm flags (over-temp, over-current, etc.)" />
          {str(v, "batt_company_id") != null && (
            <InfoChip hint="Battery vendor / company ID (1 = Battery Smart, 0 = Aphelion)">
              vendor {str(v, "batt_company_id")}
            </InfoChip>
          )}
          {num(v, "batt_load_status") != null && <InfoChip hint="Battery load / charge state reported by the BMS">load {num(v, "batt_load_status")}</InfoChip>}
        </div>
      </Panel>

      {/* ═══════════ TIER 2: drive detail ═══════════ */}
      <Panel title="Drive"
        faults={faultCount(v, ["motor_err_bits", "mctrl_err_bits", "throttle_err_bits"])}>
        <div style={row(118)}>
          <Secondary label="Motor RPM" value={fmt(num(v, "motor_rpm") ?? "—")} />
          <Secondary label="Gear" value={dash(num(v, "gear"))} />
          <Secondary label="Motor temp" value={fmt(deci(v, "motor_temp_decic") ?? "—")} unit="°C" />
          <Secondary label="Ctrl temp" value={fmt(deci(v, "mctrl_temp_decic") ?? "—")} unit="°C" />
          <Secondary label="Supply" value={num(v, "supply_v_mv") != null ? (num(v, "supply_v_mv")! / 1000).toFixed(1) : "—"} unit="V" />
          <Secondary label="Trip" value={num(v, "trip_m") != null ? (num(v, "trip_m")! / 1000).toFixed(1) : "—"} unit="km" />
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 14 }}>
          <FaultChip label="Motor" bits={num(v, "motor_err_bits")} hint="Motor fault register" />
          <FaultChip label="Ctrl" bits={num(v, "mctrl_err_bits")} hint="Motor-controller fault register" />
          <FaultChip label="Throttle" bits={num(v, "throttle_err_bits")} hint="Throttle fault register" />
          {num(v, "status_bits") != null && <InfoChip hint="Vehicle status bitfield (hex): headlight / indicators / body-IO flags">status 0x{(num(v, "status_bits") as number).toString(16)}</InfoChip>}
          {num(v, "motion_flags") != null && <InfoChip hint="Motion flags (hex): moving / parked / brake state">motion 0x{(num(v, "motion_flags") as number).toString(16)}</InfoChip>}
        </div>
      </Panel>

      {/* ═══════════ TIER 3: identity ═══════════
          FOLDED AWAY, and last. Nine fields that are true for months: an IMEI
          is the same tomorrow, a board ID was decided at manufacture. They
          were nine blue-underlined rows at the top of the page, which made
          the most static block on it the loudest. */}
      <details className="panel" style={{ marginBottom: 16 }}>
        <summary style={{ padding: "13px 20px", cursor: "pointer", fontSize: 11, fontWeight: 700,
                          letterSpacing: 0.9, textTransform: "uppercase", color: "var(--t-body)" }}>
          Device identity
          <span className="muted" style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400, marginLeft: 8 }}>
            board, cluster, versions
          </span>
        </summary>
        <div style={{ padding: "4px 20px 16px", ...row(240, "0 28px") }}>
          <Reference label="IMEI" value={l.imei} />
          <Reference label="Board ID" value={l.board_id} />
          <Reference label="Cluster ID" value={str(v, "cluster_id")} />
          <Reference label="Vehicle ID" value={str(v, "vehicle_id")} />
          <Reference label="Registration" value={l.vehicle_reg} />
          <Reference label="HW version" value={l.hw_rev ?? (num(v, "hw_version") != null ? String(num(v, "hw_version")) : null)} />
          <Reference label="App version" value={num(v, "app_sw_version") ?? l.fw_ver} />
          <Reference label="Boot version" value={num(v, "boot_sw_version")} />
          <Reference label="State" value={l.device_state} mono={false} />
          <Reference label="Type" value={l.device_type} mono={false} />
        </div>
      </details>
    </>
  );
}


/** Live-tailing decoded record table (newest on top) — the tail itself is the
 *  shared useCursorTail hook (single-flight, hidden-guard, dedupe, bounded). */
function LiveTail({ imei }: { imei: string }) {
  const [signals, setSignals] = useState<AssetDataSignal[]>([]);
  const [paused, setPaused] = useState(false);

  const { items: records, clear } = useCursorTail<AssetDataRecord>({
    fetch: async (cursor) => {
      const { records: batch, signals: sig } = await assetData.records(imei, cursor as string | undefined);
      // signals = the static catalog, sent with every response — adopt it ONCE
      // (fresh identity every tick re-rendered the whole table; the render-storm bug)
      if (sig.length) setSignals((prev) => (prev.length ? prev : sig));
      return batch;
    },
    cursorOf: (r) => r.cursor,
    max: MAX_ROWS,
    paused,
    resetKey: imei,
  });

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "14px 0 8px" }}>
        <div className="section-label flush">Decoded records (live tail)</div>
        {paused && <span style={{ color: "var(--warn)", fontWeight: 700, fontSize: 11 }}>❚❚ PAUSED</span>}
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: "var(--t-mute)" }}>{records.length} rows</span>
        <button className={`btn ${paused ? "teal" : "ghost"}`} onClick={() => setPaused((p) => !p)}>
          <Icon name={paused ? "play" : "pause"} /> {paused ? "Resume" : "Pause"}
        </button>
        <button className="btn ghost" onClick={clear}>Clear</button>
      </div>
      <RecordTable records={records} signals={signals} newestFirst />
    </div>
  );
}

export function AssetDataWindowPage() {
  // Sticky across navigation: leave Live Data and come back → same device.
  const [imei, setImei] = useSticky("live", "imei", "");
  // ...but `?imei=` WINS, once. That is how the fleet map hands a device over:
  // click a dot, land here already watching it. The param is then cleared, so
  // a later pick from the picker is not fought by a stale URL on re-render.
  const [params, setParams] = useSearchParams();
  const deep = params.get("imei");
  useEffect(() => {
    if (!deep) return;
    setImei(deep);
    setParams((prev) => { const p = new URLSearchParams(prev); p.delete("imei"); return p; },
              { replace: true });
  }, [deep]);
  return (
    <div className="mpage on">
      <PageHead eyebrow="Asset Data · Live Window" title="Watch a device"
        desc={<>Pick a company and IMEI to watch its decoded telemetry - device identity, battery and motor dials up top, then a live-tailing record table.</>} />

      <div className="panel panel-pad" style={{ marginBottom: 14 }}>
        <DevicePicker imei={imei} onPick={setImei} />
      </div>

      {imei ? (
        <>
          <Dashboard imei={imei} />
          <LiveTail imei={imei} />
        </>
      ) : (
        <div className="panel panel-pad cap">
          Select a device above to start watching.
        </div>
      )}
    </div>
  );
}
